import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;

      await axios.post(`${API}/contact`, formData);

      toast.success('Message envoyé !', {
        description: 'Nous vous répondrons dans les plus brefs délais.',
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur', {
        description: error.response?.data?.detail || "Une erreur est survenue. Veuillez réessayer.",
      });
    }
  };

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gray-900 text-white py-12 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Contactez-nous</h1>
          <p className="text-base sm:text-xl">Notre équipe est à votre écoute</p>
        </div>
      </section>

      {/* Contact content */}
      <section className="container mx-auto px-4 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Contact form */}
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="06 XX XX XX XX"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Sujet *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="Objet de votre demande"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#E42313]"
                  placeholder="Décrivez votre projet ou votre demande..."
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#E42313] text-white px-8 py-4 rounded hover:bg-[#C11A0F] transition-colors font-semibold text-lg"
              >
                Envoyer le message
              </button>
            </form>
          </div>

          {/* Contact info */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Nos coordonnées</h2>
            <div className="bg-gray-50 p-8 rounded-lg space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E42313] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#E42313]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Siège social</h3>
                  <p className="text-gray-700 font-semibold">NEOPLIAGE</p>
                  <p className="text-gray-700">23 Avenue de la Libération</p>
                  <p className="text-gray-700">01500 Ambérieu-en-Bugey</p>
                  <p className="text-gray-700">France</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E42313] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#E42313]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Retrait des commandes</h3>
                  <p className="text-gray-700">17 rue du Dépôt</p>
                  <p className="text-gray-700">01500 Ambérieu-en-Bugey</p>
                  <p className="text-xs text-gray-500 mt-1">Sur rendez-vous après confirmation de commande</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E42313] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#E42313]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Téléphone</h3>
                  <p className="text-gray-700">06.37.06.03.16</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E42313] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#E42313]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                  <p className="text-gray-700">contact@neopliage.fr</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E42313] bg-opacity-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#E42313]" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Horaires</h3>
                  <p className="text-gray-700">Lundi - Vendredi : 8h00 - 12h00 / 14h00 - 18h00</p>
                  <p className="text-gray-700">Samedi - Dimanche : Fermé</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-8 rounded-lg overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44576.77738895308!2d5.332749!3d45.960985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478b8f7c8f7c8f7d%3A0x4f7c8f7c8f7c8f7e!2s01500%20Amb%C3%A9rieu-en-Bugey!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="NEOMAT Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
